import React, { useEffect, useState } from 'react';
import styles from './ProductionReviewPage.module.css';
import LineOutGraphs from '../../components/LineOutGraphs/LineOutGraphs';
import SequenceAdherenceGraphs from '../../components/SequenceAdherenceGraphs/SequenceAdherenceGraphs';
import PlanVsActualGraphs from '../../components/PlanVsActualGraphs/PlanVsActualGraphs';
import ProductionReviewDashboardMenu from '../../components/ProductionReviewDashboardMenu/ProductionReviewDashboardMenu';
import GraphTabbedWorkspace from '../../components/GraphTabbedWorkspace/GraphTabbedWorkspace';
import { API_RESOURCE_URLS, TIME_DURATION } from '../../constants';
import {
  calculateStartDateTimeWithZoneInfo,
  calculateEndDateTimeWithZoneInfo,
} from '../../utils/graphDataUtils';
import { EXCEPTION_GRAPH_TYPE, PRODUCTION_REVIEW_TABS } from '../../staticData';
import ExceptionGraphs from '../../components/ExceptionGraphs/ExceptionGraphs';
import PlanRevisionGraphs from '../../components/PlanRevisionGraphs/PlanRevisionGraphs.js';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { API } from '../../apis/api';

const ProductionReviewPage = () => {
  const [plant, setPlant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  //assume last week
  const [durationText, setDurationText] = useState(
    TIME_DURATION.LAST_SEVEN_DAYS,
  );

  const [startDate, setStartDate] = useState(
    calculateStartDateTimeWithZoneInfo(6),
  );
  const [endDate, setEndDate] = useState(calculateEndDateTimeWithZoneInfo());

  const [selectedExceptionGraph, setSelectedExceptionGraph] = useState(
    EXCEPTION_GRAPH_TYPE.EMERGENCY_ASN,
  );

  const [plantForSequenceAdherence, setPlantForSequenceAdherence] = useState({
    value: '9000',
    label: 'ALL',
  });

  const [lineForSequenceAdherence, setLineForSequenceAdherence] = useState({
    value: '9000',
    label: 'ALL',
  });

  const setPlantValueForSequenceAdherence = value => {
    setPlantForSequenceAdherence(value);
    setLineForSequenceAdherence({ value: '9000', label: 'ALL' });
    if (value?.label !== 'ALL') {
      setPlant(value);
    }
  };

  const setLineValueForSequenceAdherence = value => {
    setLineForSequenceAdherence(value);
  };

  const [puneChassisLines, setPuneChassisLines] = useState([]);
  const [jamshedpurChassisLines, setJamshedpurChassisLines] = useState([]);
  const [dharwadChassisLines, setDharwadChassisLines] = useState([]);
  const [lukhnowChassisLines, setLukhnowChassisLines] = useState([]);

  const fetchLinesForAPlant = async plant => {
    const linesForAPlant = await API.get(API_RESOURCE_URLS.lines(plant?.value));
    const mainLines = linesForAPlant?.data.filter(
      record => record.lineType === 'Main' && record.shopType === 'Chassis',
    );
    const linesWithValueLabel = mainLines.map(({ line, lineName }) => ({
      value: line,
      label: lineName,
    }));
    return linesWithValueLabel;
  };

  useEffect(() => {
    fetchLinesForAPlant({ value: 1001 }).then(res => setPuneChassisLines(res));
    fetchLinesForAPlant({ value: 2001 }).then(res =>
      setJamshedpurChassisLines(res),
    );
    fetchLinesForAPlant({ value: 1500 }).then(res =>
      setDharwadChassisLines(res),
    );
    fetchLinesForAPlant({ value: 3001 }).then(res =>
      setLukhnowChassisLines(res),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    {
      label: PRODUCTION_REVIEW_TABS.SEQUENCE_ADHERENCE,
      component: SequenceAdherenceGraphs,
      props: {
        durationText: durationText,
        startDateTime: startDate,
        endDateTime: endDate,
        plant: plantForSequenceAdherence,
        line: lineForSequenceAdherence,
        puneChassisLines: puneChassisLines,
        jamshedpurChassisLines: jamshedpurChassisLines,
        dharwadChassisLines: dharwadChassisLines,
        lukhnowChassisLines: lukhnowChassisLines,
      },
    },
    {
      label: PRODUCTION_REVIEW_TABS.PLAN_REVISIONS,
      component: PlanRevisionGraphs,
      props: {
        plant: plant.value,
        line: line.value,
        durationText: durationText,
        startDateTime: startDate,
        endDateTime: endDate,
      },
    },
    {
      label: PRODUCTION_REVIEW_TABS.PLAN_VS_ACTUAL,
      component: PlanVsActualGraphs,
      props: {
        plant: plant.value,
        line: line.value,
        durationText: durationText,
        startDateTime: startDate,
        endDateTime: endDate,
      },
    },
    {
      label: PRODUCTION_REVIEW_TABS.LINE_OUT,
      component: LineOutGraphs,
      props: {
        plant: plant.value,
        line: line.value,
        durationText: durationText,
        startDateTime: startDate,
        endDateTime: endDate,
      },
    },
    {
      label: PRODUCTION_REVIEW_TABS.EXCEPTION,
      component: ExceptionGraphs,
      props: {
        plant: plant.value,
        line: line.value,
        durationText: durationText,
        startDateTime: startDate,
        endDateTime: endDate,
        selectedGraph: selectedExceptionGraph,
        handleGraphSelection: setSelectedExceptionGraph,
      },
    },
  ];
  const [tabVal, setTabVal] = useState('');
  const newFunc = tabVal => {
    setTabVal(tabVal);
  };

  const isSequenceAdherenceTabSelected = tabVal => {
    const tabValue = tabVal === 0 ? 'Seq. Adherence' : null;
    return tabValue;
  };

  return (
    <>
      <div className={styles.mainBody}>
        <div className={styles.mainWraper}>
          <div className={styles.leftDashBoardMenu}>
            <ProductionReviewDashboardMenu
              handleDurationTextChange={setDurationText}
              handleStartDateChange={setStartDate}
              handleEndDateChange={setEndDate}
              sequenceAdherenceDashBoard={isSequenceAdherenceTabSelected(
                tabVal,
              )}
              setPlantValueForSequenceAdherence={
                setPlantValueForSequenceAdherence
              }
              setLineValueForSequenceAdherence={
                setLineValueForSequenceAdherence
              }
              plantValue={plantForSequenceAdherence}
            />
          </div>
          <div className={styles.rightGraphsBody}>
            <div className="graphContainer">
              <GraphTabbedWorkspace
                tabs={tabs}
                setValue={newFunc}
                // handleRefreshButtonCLick={loadRawDataForAllGraphs}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ProductionReviewPage;
